import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { GraduationCap, Lock, Rocket, ChevronRight, Loader2, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CourseCard = ({ course, locked, onClick }) => {
  const pct = course.chapter_count > 0 ? Math.round((course.chapters_completed / course.chapter_count) * 100) : 0;
  return (
    <div
      onClick={onClick}
      data-testid={`course-card-${course.id}`}
      className="group relative overflow-hidden rounded-xl border p-5 cursor-pointer transition hover:-translate-y-0.5 motion-fade-up"
      style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl">{course.emoji}</span>
        {locked ? (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest border"
            style={{
              background: 'rgba(245,158,11,0.10)',
              borderColor: 'rgba(245,158,11,0.35)',
              color: 'rgb(var(--warning))',
            }}
          >
            <Lock className="w-3 h-3" /> Pro
          </span>
        ) : (
          <ChevronRight className="w-4 h-4 text-fg-faint group-hover:text-accent group-hover:translate-x-1 transition-all" />
        )}
      </div>
      <h3 className="font-semibold text-fg mb-1">{course.title}</h3>
      <p className="text-xs text-fg-muted mb-4 line-clamp-2">{course.description}</p>
      <div className="flex items-center justify-between text-[11px] text-fg-muted mb-1.5">
        <span>{course.chapters_completed}/{course.chapter_count} {course.type === 'crash' ? 'days' : 'chapters'}</span>
        <span className="font-semibold text-fg">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: 'rgb(var(--accent))' }} />
      </div>
    </div>
  );
};

export default function Courses() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [track, setTrack] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/courses`).then(({ data }) => {
      setData(data);
      setTrack(data.exam_track === 'NEET' ? 'NEET' : 'JEE');
    }).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" /></div>
      </div>
    );
  }

  const courses = data.courses.filter(c => c.track === track);
  const subjectCourses = courses.filter(c => c.type === 'subject');
  const crashCourses = courses.filter(c => c.type === 'crash');
  const open = (c) => {
    if (c.locked) { navigate('/upgrade'); return; }
    navigate(`/courses/${c.id}`);
  };

  return (
    <div className="min-h-screen" data-testid="courses-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 motion-fade-up">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-fg-faint font-medium mb-2">
              <GraduationCap className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
              <span>Curriculum</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-fg tracking-tight">AI Courses</h1>
            <p className="text-sm text-fg-muted mt-1">Video lessons, chapter quizzes and crash courses — engineered for 97+ percentile.</p>
          </div>
          <div className="flex rounded-md border p-1" style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }} data-testid="track-toggle">
            {['JEE', 'NEET'].map(t => (
              <button
                key={t}
                onClick={() => setTrack(t)}
                data-testid={`track-toggle-${t.toLowerCase()}`}
                className={`px-5 py-1.5 rounded text-sm font-semibold transition ${track === t ? 'text-white' : 'text-fg-muted hover:text-fg'}`}
                style={track === t ? { background: 'rgb(var(--accent))' } : {}}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {!data.is_pro && (
          <div
            className="mb-6 p-4 rounded-lg border flex flex-col sm:flex-row sm:items-center gap-3 motion-fade-up"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.08) 100%)',
              borderColor: 'rgba(239,68,68,0.35)',
            }}
            data-testid="courses-pro-banner"
          >
            <Crown className="w-5 h-5 shrink-0" style={{ color: 'rgb(var(--accent))' }} />
            <div className="flex-1">
              <p className="font-semibold text-sm text-fg">AI Courses are a Pro feature</p>
              <p className="text-xs text-fg-muted">Unlock every chapter lesson, mastery quiz and the 15-day crash course — from under ₹7/day.</p>
            </div>
            <button onClick={() => navigate('/upgrade')}
              className="shrink-0 px-4 py-2 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
              style={{ background: 'rgb(var(--accent))' }}
              data-testid="courses-upgrade-btn"
            >
              Unlock courses →
            </button>
          </div>
        )}

        <h2 className="text-[11px] uppercase tracking-widest text-fg-faint font-medium mb-3 flex items-center gap-2">
          <Rocket className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} /> Crash Course
        </h2>
        {!data.is_pro && (
          <div
            onClick={() => navigate('/crash-course')}
            className="mb-4 p-4 rounded-xl border cursor-pointer flex items-center gap-3 transition hover:-translate-y-0.5 motion-fade-up"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.10) 100%)',
              borderColor: 'rgba(239,68,68,0.35)',
            }}
            data-testid="crash-course-standalone-cta"
          >
            <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
              style={{ background: 'rgba(239,68,68,0.15)' }}>
              <Rocket className="w-4 h-4" style={{ color: 'rgb(var(--accent))' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-fg">Just want the 15-day sprint? <span style={{ color: 'rgb(var(--accent))' }}>Buy the crash-course pass — ₹499</span></p>
              <p className="text-xs text-fg-muted mt-0.5">One-time · 30-day access · No monthly commitment</p>
            </div>
            <ChevronRight className="w-4 h-4 text-fg-faint shrink-0" />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {crashCourses.map(c => <CourseCard key={c.id} course={c} locked={c.locked} onClick={() => open(c)} />)}
        </div>

        <h2 className="text-[11px] uppercase tracking-widest text-fg-faint font-medium mb-3">Subject Mastery</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {subjectCourses.map(c => <CourseCard key={c.id} course={c} locked={c.locked} onClick={() => open(c)} />)}
        </div>
      </main>
    </div>
  );
}
